import ArrowForwardRoundedIcon from '@mui/icons-material/ArrowForwardRounded';
import HourglassBottomIcon from '@mui/icons-material/HourglassBottom';
import Inventory2Icon from '@mui/icons-material/Inventory2';
import LiveHelpIcon from '@mui/icons-material/LiveHelp';
import LockIcon from '@mui/icons-material/Lock';
import VerifiedIcon from '@mui/icons-material/Verified';
import VerifiedUserIcon from '@mui/icons-material/VerifiedUser';
import WarningIcon from '@mui/icons-material/Warning';
import WorkspacePremiumIcon from '@mui/icons-material/WorkspacePremium';
import { Box, Link, Stack, Tooltip, Typography } from '@mui/material';
import { useState } from 'react';
import * as UI from '@siloreal:shared:ui';
import type { SilobagArchivedListResponse } from '@siloreal:shared:api/service/silobag/silobag-archived-list';
import { useSilobagListContext } from '../../context';
import * as HOOKS from '@siloreal:shared:hooks';

export const TABLE_COUNTER = {
  SILOS_PRO: {
    VRR: VrrCounterGroup,
    WITHOUT_VRR: WithoutVrrCounterGroup,
    CLOSED: SilosProClosedCounterGroup,
  },
  SILOBAG: {
    ACTIVE: SilobagActiveCounterGroup,
    CLOSED: SilobagClosedCounterGroup,
    ARCHIVED: SilobagArchivedCounterGroup,
  },
} as const;

const COUNTER_CARD_HEIGHT = 64;

function useCounterStats() {
  const {
    state: { current: state },
    async: {
      list: { current: list },
      summary: { current: summary },
    },
  } = useSilobagListContext();

  const archived =
    state.view === 'silobag' && state.tab === 'archived'
      ? (list as unknown as SilobagArchivedListResponse | null)
      : null;

  return {
    total: formatCount(summary?.totalFilteredSilobags ?? archived?.totalFilteredSilobags),
    remainingStock: formatTons(summary?.filteredRemainingStock),
    inputWeight: formatTons(
      summary?.filteredInputWeight ?? (archived ? Number(archived.filteredInputWeight) : undefined)
    ),
    extractedWeight: formatTons(summary?.filteredExtractedWeight ?? archived?.filteredExtractedWeight),
    stockDifference: formatTons(summary?.filteredRemainingStock),
    stockPercentage: formatPercentage(summary?.filteredRemainingStock, summary?.totalRemainingStock),
    inconsistencyCount: formatCount(summary?.totalFilteredDiscrepancies),
    insuranceCount: formatCount(summary?.insurance_silobags_count),
    insuranceStock: formatTons(summary?.insurance_silobags_stock),
    insurancePercentage: formatPercentage(summary?.insurance_silobags_stock, summary?.totalRemainingStock),
    warrantCount: formatCount(summary?.warrant_silobags_count),
    warrantStock: formatTons(summary?.warrant_silobags_stock),
    warrantPercentage: formatPercentage(summary?.warrant_silobags_stock, summary?.totalRemainingStock),
  };
}

function formatTons(value = 0): string {
  return formatCount(Math.round(value / 1000));
}

function formatPercentage(value = 0, total = 0): string {
  if (!total) return '0%';
  return `${Math.ceil((value / total) * 100 * 10) / 10}%`;
}

function formatCount(value = 0): string {
  return new Intl.NumberFormat('es-AR', { maximumFractionDigits: 0 }).format(value);
}

function VrrCounterGroup() {
  const stats = useCounterStats();

  const i18n = HOOKS.useLocale();

  return (
    <Stack direction="row" alignItems="stretch" flexWrap="wrap" gap={2}>
      <Box sx={{ flex: '0 1 auto' }}>
        <UI.Card variant="spotlight">
          <Box
            px={2.5}
            sx={{
              height: COUNTER_CARD_HEIGHT,
              display: 'flex',
              alignItems: 'center',
            }}
          >
            <Stack
              direction="row"
              alignItems="center"
              justifyContent="space-between"
              gap={3}
              sx={{ color: '#42db37' }}
            >
              <Stack sx={{ minWidth: 88 }}>
                <Stack direction="row" alignItems="center" gap={0.75}>
                  <Typography fontSize={22} fontWeight={900} color="#3A3440" lineHeight={1}>
                    {stats.total}
                  </Typography>
                  <Box aria-hidden sx={{ display: 'grid', fontSize: 15, lineHeight: 0 }}>
                    <VerifiedIcon fontSize="inherit" />
                  </Box>
                </Stack>
                <Typography fontSize={12} color="#5E5964" lineHeight={1.25}>
                  {i18n.t('temp.private.list.silospro.with_vrr')}
                </Typography>
              </Stack>

              <Stack sx={{ minWidth: 88 }}>
                <Stack direction="row" alignItems="center" gap={0.75}>
                  <Typography fontSize={22} fontWeight={900} color="#3A3440" lineHeight={1}>
                    {stats.remainingStock}
                  </Typography>
                </Stack>
                <Typography fontSize={12} color="#5E5964" lineHeight={1.25}>
                  {i18n.t('temp.common.tons')}
                </Typography>
              </Stack>

              <Stack sx={{ minWidth: 88 }}>
                <Stack direction="row" alignItems="center" gap={0.75}>
                  <Typography fontSize={22} fontWeight={900} color="#3A3440" lineHeight={1}>
                    {stats.stockPercentage}
                  </Typography>
                </Stack>
                <Typography fontSize={12} color="#5E5964" lineHeight={1.25}>
                  {i18n.t('temp.private.list.silospro.from_your_silospro_stock')}
                </Typography>
              </Stack>
            </Stack>
          </Box>
        </UI.Card>
      </Box>

      <Box sx={{ flex: '0 1 auto' }}>
        <UI.Card variant="major">
          <Box
            px={2.5}
            sx={{
              height: COUNTER_CARD_HEIGHT,
              display: 'flex',
              alignItems: 'center',
            }}
          >
            <Stack
              direction="row"
              alignItems="center"
              justifyContent="space-between"
              gap={3}
              sx={{ color: '#9B59B6' }}
            >
              <Stack sx={{ minWidth: 88 }}>
                <Stack direction="row" alignItems="center" gap={0.75}>
                  <Typography fontSize={22} fontWeight={900} color="#3A3440" lineHeight={1}>
                    {stats.insuranceCount}
                  </Typography>
                  <Box aria-hidden sx={{ display: 'grid', fontSize: 15, lineHeight: 0 }}>
                    <VerifiedUserIcon fontSize="inherit" />
                  </Box>
                </Stack>
                <Typography fontSize={12} color="#5E5964" lineHeight={1.25}>
                  {i18n.t('temp.private.list.silospro.insured')}
                </Typography>
              </Stack>

              <Stack sx={{ minWidth: 88 }}>
                <Stack direction="row" alignItems="center" gap={0.75}>
                  <Typography fontSize={22} fontWeight={900} color="#3A3440" lineHeight={1}>
                    {stats.insuranceStock}
                  </Typography>
                </Stack>
                <Typography fontSize={12} color="#5E5964" lineHeight={1.25}>
                  {i18n.t('temp.common.tons')}
                </Typography>
              </Stack>

              <Stack sx={{ minWidth: 88 }}>
                <Stack direction="row" alignItems="center" gap={0.75}>
                  <Typography fontSize={22} fontWeight={900} color="#3A3440" lineHeight={1}>
                    {stats.insurancePercentage}
                  </Typography>
                </Stack>
                <Typography fontSize={12} color="#5E5964" lineHeight={1.25}>
                  {i18n.t('temp.private.list.silospro.from_your_silospro_stock')}
                </Typography>
              </Stack>
            </Stack>
          </Box>
        </UI.Card>
      </Box>

      <Box sx={{ flex: '0 1 auto' }}>
        <UI.Card variant="major">
          <Box
            px={2.5}
            sx={{
              height: COUNTER_CARD_HEIGHT,
              display: 'flex',
              alignItems: 'center',
            }}
          >
            <Stack
              direction="row"
              alignItems="center"
              justifyContent="space-between"
              gap={3}
              sx={{ color: '#9B59B6' }}
            >
              <Stack sx={{ minWidth: 88 }}>
                <Stack direction="row" alignItems="center" gap={0.75}>
                  <Typography fontSize={22} fontWeight={900} color="#3A3440" lineHeight={1}>
                    {stats.warrantCount}
                  </Typography>
                  <Box aria-hidden sx={{ display: 'grid', fontSize: 15, lineHeight: 0 }}>
                    <WorkspacePremiumIcon fontSize="inherit" />
                  </Box>
                </Stack>
                <Typography fontSize={12} color="#5E5964" lineHeight={1.25}>
                  {i18n.t('temp.private.list.silospro.guarantees')}
                </Typography>
              </Stack>

              <Stack sx={{ minWidth: 88 }}>
                <Stack direction="row" alignItems="center" gap={0.75}>
                  <Typography fontSize={22} fontWeight={900} color="#3A3440" lineHeight={1}>
                    {stats.warrantStock}
                  </Typography>
                </Stack>
                <Typography fontSize={12} color="#5E5964" lineHeight={1.25}>
                  {i18n.t('temp.common.tons')}
                </Typography>
              </Stack>

              <Stack sx={{ minWidth: 88 }}>
                <Stack direction="row" alignItems="center" gap={0.75}>
                  <Typography fontSize={22} fontWeight={900} color="#3A3440" lineHeight={1}>
                    {stats.warrantPercentage}
                  </Typography>
                </Stack>
                <Typography fontSize={12} color="#5E5964" lineHeight={1.25}>
                  {i18n.t('temp.private.list.silospro.from_your_silospro_stock')}
                </Typography>
              </Stack>
            </Stack>
          </Box>
        </UI.Card>
      </Box>

      <Box sx={{ flex: '0 1 auto' }}>
        <UI.Card variant="danger">
          <Box
            px={2.5}
            sx={{
              height: COUNTER_CARD_HEIGHT,
              display: 'flex',
              alignItems: 'center',
            }}
          >
            <Stack
              direction="row"
              alignItems="center"
              justifyContent="flex-start"
              gap={3}
              sx={{ color: '#ea1818' }}
            >
              <Stack sx={{ minWidth: 88 }}>
                <Stack direction="row" alignItems="center" gap={0.75}>
                  <Typography fontSize={22} fontWeight={900} color="#3A3440" lineHeight={1}>
                    {stats.inconsistencyCount}
                  </Typography>
                  <Box aria-hidden sx={{ display: 'grid', fontSize: 15, lineHeight: 0 }}>
                    <WarningIcon fontSize="inherit" />
                  </Box>
                </Stack>
                <Typography fontSize={12} color="#5E5964" lineHeight={1.25}>
                  {i18n.t('temp.common.inconsistencies')}
                </Typography>
              </Stack>
            </Stack>
          </Box>
        </UI.Card>
      </Box>
    </Stack>
  );
}

function WithoutVrrCounterGroup() {
  const stats = useCounterStats();

  const i18n = HOOKS.useLocale();

  return (
    <Stack direction="row" alignItems="stretch" flexWrap="wrap" gap={2}>
      <Box sx={{ flex: '0 1 auto' }}>
        <UI.Card>
          <Box
            px={2.5}
            sx={{
              height: COUNTER_CARD_HEIGHT,
              display: 'flex',
              alignItems: 'center',
            }}
          >
            <Stack
              direction="row"
              alignItems="center"
              justifyContent="space-between"
              gap={3}
              sx={{ color: '#CFCFD4' }}
            >
              <Stack sx={{ minWidth: 88 }}>
                <Stack direction="row" alignItems="center" gap={0.75}>
                  <Typography fontSize={22} fontWeight={900} color="#3A3440" lineHeight={1}>
                    {stats.total}
                  </Typography>
                  <Box aria-hidden sx={{ display: 'grid', fontSize: 15, lineHeight: 0 }}>
                    <VerifiedIcon fontSize="inherit" />
                  </Box>
                </Stack>
                <Typography fontSize={12} color="#5E5964" lineHeight={1.25}>
                  {i18n.t('temp.private.list.silospro.without_vrr')}
                </Typography>
              </Stack>

              <Stack sx={{ minWidth: 88 }}>
                <Stack direction="row" alignItems="center" gap={0.75}>
                  <Typography fontSize={22} fontWeight={900} color="#3A3440" lineHeight={1}>
                    {stats.remainingStock}
                  </Typography>
                </Stack>
                <Typography fontSize={12} color="#5E5964" lineHeight={1.25}>
                  {i18n.t('temp.common.tons')}
                </Typography>
              </Stack>

              <Stack sx={{ minWidth: 88 }}>
                <Stack direction="row" alignItems="center" gap={0.75}>
                  <Typography fontSize={22} fontWeight={900} color="#3A3440" lineHeight={1}>
                    {stats.stockPercentage}
                  </Typography>
                </Stack>
                <Typography fontSize={12} color="#5E5964" lineHeight={1.25}>
                  {i18n.t('temp.private.list.silospro.from_your_silospro_stock')}
                </Typography>
              </Stack>
            </Stack>
          </Box>
        </UI.Card>
      </Box>

      <Box sx={{ flex: '0 1 auto' }}>
        <UI.Card variant="danger">
          <Box
            px={2.5}
            sx={{
              height: COUNTER_CARD_HEIGHT,
              display: 'flex',
              alignItems: 'center',
            }}
          >
            <Stack
              direction="row"
              alignItems="center"
              justifyContent="flex-start"
              gap={3}
              sx={{ color: '#ea1818' }}
            >
              <Stack sx={{ minWidth: 88 }}>
                <Stack direction="row" alignItems="center" gap={0.75}>
                  <Typography fontSize={22} fontWeight={900} color="#3A3440" lineHeight={1}>
                    {stats.inconsistencyCount}
                  </Typography>
                  <Box aria-hidden sx={{ display: 'grid', fontSize: 15, lineHeight: 0 }}>
                    <WarningIcon fontSize="inherit" />
                  </Box>
                </Stack>
                <Typography fontSize={12} color="#5E5964" lineHeight={1.25}>
                  {i18n.t('temp.common.inconsistencies')}
                </Typography>
              </Stack>
            </Stack>
          </Box>
        </UI.Card>
      </Box>
    </Stack>
  );
}

function SilosProClosedCounterGroup() {
  const stats = useCounterStats();

  const i18n = HOOKS.useLocale();

  return (
    <Stack direction="row" alignItems="stretch" flexWrap="wrap" gap={2}>
      <Box sx={{ flex: '0 1 auto' }}>
        <UI.Card>
          <Box
            px={2.5}
            sx={{
              height: COUNTER_CARD_HEIGHT,
              display: 'flex',
              alignItems: 'center',
            }}
          >
            <Stack
              direction="row"
              alignItems="center"
              justifyContent="space-between"
              gap={3}
              sx={{ color: '#49454F' }}
            >
              <Stack sx={{ minWidth: 88 }}>
                <Stack direction="row" alignItems="center" gap={0.75}>
                  <Typography fontSize={22} fontWeight={900} color="#3A3440" lineHeight={1}>
                    {stats.total}
                  </Typography>
                  <Box aria-hidden sx={{ display: 'grid', fontSize: 15, lineHeight: 0 }}>
                    <LockIcon fontSize="inherit" />
                  </Box>
                </Stack>
                <Typography fontSize={12} color="#5E5964" lineHeight={1.25}>
                  {i18n.t('temp.common.closed')}
                </Typography>
              </Stack>

              <Stack sx={{ minWidth: 88 }}>
                <Stack direction="row" alignItems="center" gap={0.75}>
                  <Typography fontSize={22} fontWeight={900} color="#3A3440" lineHeight={1}>
                    {stats.inputWeight}
                  </Typography>
                </Stack>
                <Typography fontSize={12} color="#5E5964" lineHeight={1.25}>
                  {i18n.t('temp.private.list.common.bagged_tons')}
                </Typography>
              </Stack>

              <Stack sx={{ minWidth: 88 }}>
                <Stack direction="row" alignItems="center" gap={0.75}>
                  <Typography fontSize={22} fontWeight={900} color="#3A3440" lineHeight={1}>
                    {stats.extractedWeight}
                  </Typography>
                </Stack>
                <Typography fontSize={12} color="#5E5964" lineHeight={1.25}>
                  {i18n.t('temp.private.list.common.extracted_tons')}
                </Typography>
              </Stack>

              <Stack sx={{ minWidth: 88 }}>
                <Stack direction="row" alignItems="center" gap={0.75}>
                  <Typography fontSize={22} fontWeight={900} color="#3A3440" lineHeight={1}>
                    {stats.stockDifference}
                  </Typography>
                </Stack>
                <Typography fontSize={12} color="#5E5964" lineHeight={1.25}>
                  {i18n.t('temp.common.difference')}
                </Typography>
              </Stack>
            </Stack>
          </Box>
        </UI.Card>
      </Box>
    </Stack>
  );
}

function SilobagActiveCounterGroup() {
  const stats = useCounterStats();
  const [isInfoModalOpen, setIsInfoModalOpen] = useState(false);
  const i18n = HOOKS.useLocale();

  const app_store_src = i18n.t('temp.private.list.silobag.silobag_info_modal.app_store_src');
  const download_in_app_store = i18n.t(
    'temp.private.list.silobag.silobag_info_modal.download_in_apple_store'
  );

  const play_store_src = i18n.t('temp.private.list.silobag.silobag_info_modal.play_store_src');
  const download_in_google_play = i18n.t(
    'temp.private.list.silobag.silobag_info_modal.download_in_google_play'
  );
  return (
    <>
      <UI.Card>
        <Stack
          direction="row"
          alignItems="center"
          justifyContent="space-between"
          gap={2}
          flexWrap="wrap"
          px={2.5}
          sx={{ minHeight: COUNTER_CARD_HEIGHT }}
        >
          <Stack
            direction="row"
            alignItems="center"
            justifyContent="space-between"
            gap={3}
            sx={{ color: '#49454F', flex: '0 1 auto' }}
          >
            <Stack sx={{ minWidth: 88 }}>
              <Stack direction="row" alignItems="center" gap={0.75}>
                <Typography fontSize={22} fontWeight={900} color="#3A3440" lineHeight={1}>
                  {stats.total}
                </Typography>
                <Box aria-hidden sx={{ display: 'grid', fontSize: 15, lineHeight: 0 }}>
                  <HourglassBottomIcon fontSize="inherit" />
                </Box>
              </Stack>
              <Typography fontSize={12} color="#5E5964" lineHeight={1.25}>
                {i18n.t('temp.common.silobags')}
              </Typography>
            </Stack>

            <Stack sx={{ minWidth: 88 }}>
              <Stack direction="row" alignItems="center" gap={0.75}>
                <Typography fontSize={22} fontWeight={900} color="#3A3440" lineHeight={1}>
                  {stats.remainingStock}
                </Typography>
              </Stack>
              <Typography fontSize={12} color="#5E5964" lineHeight={1.25}>
                {i18n.t('temp.common.tons')}
              </Typography>
            </Stack>

            <Stack sx={{ minWidth: 88 }}>
              <Stack direction="row" alignItems="center" gap={0.75}>
                <Typography fontSize={22} fontWeight={900} color="#3A3440" lineHeight={1}>
                  {stats.stockPercentage}
                </Typography>
              </Stack>
              <Typography fontSize={12} color="#5E5964" lineHeight={1.25}>
                {i18n.t('temp.private.list.silobag.from_your_silobags_stock')}
              </Typography>
            </Stack>
          </Stack>

          <Stack alignItems="flex-start" gap={0.5} sx={{ flex: '0 1 460px' }}>
            <Box>
              <Typography fontSize={12} fontWeight={600} color="#3A3440" lineHeight={1.2}>
                {i18n.t('temp.private.list.silobag.validate_your_silobags_at_origin')}
              </Typography>
              <Typography fontSize={11} color="#5E5964" lineHeight={1.2}>
                {i18n.t('temp.private.list.silobag.secure_with_a_single_step')}
              </Typography>
            </Box>
            <UI.Button
              variant="alternative-hollow"
              removeBorder
              size="small"
              noPadding
              onClick={() => setIsInfoModalOpen(true)}
              icon={{ value: <ArrowForwardRoundedIcon sx={{ fontSize: 12 }} />, position: 'end' }}
              tid="silobag-list-summary-know-more-btn"
            >
              {i18n.t('temp.common.learn_more')}
            </UI.Button>
          </Stack>
        </Stack>
      </UI.Card>

      <UI.Modal
        open={isInfoModalOpen}
        onClose={() => setIsInfoModalOpen(false)}
        title={
          <Typography fontSize={26} fontWeight={800} color="#49454F" lineHeight={1.35}>
            {i18n.t('temp.private.list.silobag.silobag_info_modal.title_1')}
          </Typography>
        }
        size="md"
        fullWidth
        showCloseButton={false}
        tid="silobag-info-modal"
        actions={
          <UI.Button
            variant="text-link"
            onClick={() => setIsInfoModalOpen(false)}
            tid="silobag-info-modal-close-btn"
          >
            {i18n.t('temp.common.close')}
          </UI.Button>
        }
      >
        <Stack gap={2}>
          <Stack gap={1.75}>
            <Typography fontSize={15} color="#49454F" lineHeight={1.5}>
              {i18n.t('temp.private.list.silobag.silobag_info_modal.content_1')}
              <Box component="span" sx={{ fontWeight: 800 }}>
                {i18n.t('temp.private.list.silobag.silobag_info_modal.content_2')}
              </Box>
              {i18n.t('temp.private.list.silobag.silobag_info_modal.content_3')}
              <Box component="span" sx={{ fontWeight: 800 }}>
                {i18n.t('temp.private.list.silobag.silobag_info_modal.content_4')}
              </Box>
              {i18n.t('temp.private.list.silobag.silobag_info_modal.content_5')}
              <Box component="span" sx={{ fontWeight: 800 }}>
                {i18n.t('temp.private.list.silobag.silobag_info_modal.content_6')}
              </Box>
              <br />
              {i18n.t('temp.private.list.silobag.silobag_info_modal.content_7')}
              <Box component="span" sx={{ fontWeight: 800 }}>
                {i18n.t('temp.private.list.silobag.silobag_info_modal.content_8')}
              </Box>
              {i18n.t('temp.private.list.silobag.silobag_info_modal.content_9')}
              <Box component="span" sx={{ fontWeight: 800 }}>
                {i18n.t('temp.private.list.silobag.silobag_info_modal.content_10')}
              </Box>
              <br />
              <Box component="span" sx={{ fontWeight: 800 }}>
                {i18n.t('temp.private.list.silobag.silobag_info_modal.content_11')}
              </Box>
            </Typography>

            <Typography fontSize={15} fontWeight={800} color="#49454F" lineHeight={1.55}>
              {i18n.t('temp.private.list.silobag.silobag_info_modal.title_advantages')}
            </Typography>

            <Stack component="ul" gap={0.5} sx={{ m: 0, pl: 0, listStyle: 'none' }}>
              <Stack component="li" direction="row" alignItems="flex-start" gap={1}>
                <Box
                  aria-hidden
                  sx={{
                    width: 7,
                    height: 7,
                    borderRadius: '50%',
                    backgroundColor: '#6BAA00',
                    mt: '8px',
                    flex: '0 0 auto',
                  }}
                />
                <Typography fontSize={15} color="#49454F" lineHeight={1.35}>
                  {i18n.t('temp.private.list.silobag.silobag_info_modal.advantage_1')}
                </Typography>
              </Stack>
              <Stack component="li" direction="row" alignItems="flex-start" gap={1}>
                <Box
                  aria-hidden
                  sx={{
                    width: 7,
                    height: 7,
                    borderRadius: '50%',
                    backgroundColor: '#6BAA00',
                    mt: '8px',
                    flex: '0 0 auto',
                  }}
                />
                <Typography fontSize={15} color="#49454F" lineHeight={1.35}>
                  {i18n.t('temp.private.list.silobag.silobag_info_modal.advantage_2_1')}
                  <Box component="span" sx={{ fontWeight: 800 }}>
                    {i18n.t('temp.private.list.silobag.silobag_info_modal.advantage_2_2')}
                  </Box>
                </Typography>
              </Stack>
              <Stack component="li" direction="row" alignItems="flex-start" gap={1}>
                <Box
                  aria-hidden
                  sx={{
                    width: 7,
                    height: 7,
                    borderRadius: '50%',
                    backgroundColor: '#6BAA00',
                    mt: '8px',
                    flex: '0 0 auto',
                  }}
                />
                <Typography fontSize={15} color="#49454F" lineHeight={1.35}>
                  <Box component="span" sx={{ fontWeight: 800 }}>
                    {i18n.t('temp.private.list.silobag.silobag_info_modal.advantage_3_1')}
                  </Box>
                  {i18n.t('temp.private.list.silobag.silobag_info_modal.advantage_3_2')}
                  <Box component="span" sx={{ fontWeight: 800 }}>
                    {i18n.t('temp.private.list.silobag.silobag_info_modal.advantage_3_3')}
                  </Box>
                </Typography>
              </Stack>
            </Stack>
          </Stack>

          <Stack gap={1.25}>
            <Typography fontSize={16} fontWeight={800} color="#49454F">
              {i18n.t('temp.private.list.silobag.silobag_info_modal.title_2')}
            </Typography>

            <Stack direction="row" alignItems="center" gap={2}>
              <Link
                href="https://play.google.com/store/apps/details?id=com.iof_mobile&hl=es_AR"
                target="_blank"
                rel="noopener noreferrer"
                aria-label="Abrir SiloReal en Google Play"
              >
                <Box
                  component="img"
                  src={play_store_src}
                  alt={download_in_google_play}
                  sx={{ height: 48, width: 'auto', display: 'block' }}
                />
              </Link>
              <Tooltip title="Proximamente">
                <Box
                  aria-label="App Store próximamente"
                  sx={{
                    height: 48,
                    display: 'block',
                    opacity: 0.45,
                    filter: 'grayscale(1)',
                    cursor: 'not-allowed',
                  }}
                >
                  <Box
                    component="img"
                    src={app_store_src}
                    alt={download_in_app_store}
                    sx={{ height: 48, width: 'auto', display: 'block' }}
                  />
                </Box>
              </Tooltip>
            </Stack>
          </Stack>

          <Stack gap={0.25}>
            <Stack direction="row" alignItems="center" gap={0.5}>
              <LiveHelpIcon sx={{ color: '#22A7F0', fontSize: 18 }} />
              <Typography fontSize={15} fontWeight={800} color="#49454F">
                {i18n.t('temp.private.list.silobag.silobag_info_modal.footer_question')}
              </Typography>
            </Stack>
            <Typography fontSize={15} color="#49454F" sx={{ pl: 2.5 }}>
              {i18n.t('temp.private.list.silobag.silobag_info_modal.footer_answer_1')}
              <Link href="#manual-de-usuario" color="#22A7F0" underline="always" fontWeight={500}>
                {i18n.t('temp.private.list.silobag.silobag_info_modal.footer_answer_2')}
              </Link>
              {i18n.t('temp.private.list.silobag.silobag_info_modal.footer_answer_3')}
            </Typography>
          </Stack>
        </Stack>
      </UI.Modal>
    </>
  );
}

function SilobagClosedCounterGroup() {
  const stats = useCounterStats();

  const i18n = HOOKS.useLocale();

  return (
    <Stack direction="row" alignItems="stretch" flexWrap="wrap" gap={2}>
      <Box
        sx={{
          flex: '0 1 auto',
          height: COUNTER_CARD_HEIGHT,
          backgroundColor: '#FFFFFF',
          borderRadius: '10px',
          boxShadow: 'inset 0 0 0 1px #DFE3EA',
        }}
      >
        <Box
          px={2.5}
          sx={{
            height: COUNTER_CARD_HEIGHT,
            display: 'flex',
            alignItems: 'center',
          }}
        >
          <Stack
            direction="row"
            alignItems="center"
            justifyContent="space-between"
            gap={3}
            sx={{ color: '#49454F' }}
          >
            <Stack sx={{ minWidth: 88 }}>
              <Stack direction="row" alignItems="center" gap={0.75}>
                <Typography fontSize={22} fontWeight={900} color="#3A3440" lineHeight={1}>
                  {stats.total}
                </Typography>
                <Box aria-hidden sx={{ display: 'grid', fontSize: 15, lineHeight: 0 }}>
                  <LockIcon fontSize="inherit" />
                </Box>
              </Stack>
              <Typography fontSize={12} color="#5E5964" lineHeight={1.25}>
                {i18n.t('temp.common.closed')}
              </Typography>
            </Stack>

            <Stack sx={{ minWidth: 88 }}>
              <Stack direction="row" alignItems="center" gap={0.75}>
                <Typography fontSize={22} fontWeight={900} color="#3A3440" lineHeight={1}>
                  {stats.inputWeight}
                </Typography>
              </Stack>
              <Typography fontSize={12} color="#5E5964" lineHeight={1.25}>
                {i18n.t('temp.private.list.common.bagged_tons')}
              </Typography>
            </Stack>

            <Stack sx={{ minWidth: 88 }}>
              <Stack direction="row" alignItems="center" gap={0.75}>
                <Typography fontSize={22} fontWeight={900} color="#3A3440" lineHeight={1}>
                  {stats.extractedWeight}
                </Typography>
              </Stack>
              <Typography fontSize={12} color="#5E5964" lineHeight={1.25}>
                {i18n.t('temp.private.list.common.extracted_tons')}
              </Typography>
            </Stack>

            <Stack sx={{ minWidth: 88 }}>
              <Stack direction="row" alignItems="center" gap={0.75}>
                <Typography fontSize={22} fontWeight={900} color="#3A3440" lineHeight={1}>
                  {stats.stockDifference}
                </Typography>
              </Stack>
              <Typography fontSize={12} color="#5E5964" lineHeight={1.25}>
                {i18n.t('temp.common.difference')}
              </Typography>
            </Stack>
          </Stack>
        </Box>
      </Box>
    </Stack>
  );
}

function SilobagArchivedCounterGroup() {
  const stats = useCounterStats();

  const i18n = HOOKS.useLocale();

  return (
    <Stack direction="row" alignItems="stretch" flexWrap="wrap" gap={2}>
      <Box
        sx={{
          flex: '0 1 auto',
          height: COUNTER_CARD_HEIGHT,
          backgroundColor: '#FFFFFF',
          borderRadius: '10px',
          boxShadow: 'inset 0 0 0 1px #DFE3EA',
        }}
      >
        <Box
          px={2.5}
          sx={{
            height: COUNTER_CARD_HEIGHT,
            display: 'flex',
            alignItems: 'center',
          }}
        >
          <Stack
            direction="row"
            alignItems="center"
            justifyContent="space-between"
            gap={3}
            sx={{ color: '#6F6B75' }}
          >
            <Stack sx={{ minWidth: 88 }}>
              <Stack direction="row" alignItems="center" gap={0.75}>
                <Typography fontSize={22} fontWeight={900} color="#3A3440" lineHeight={1}>
                  {stats.total}
                </Typography>
                <Box aria-hidden sx={{ display: 'grid', fontSize: 15, lineHeight: 0 }}>
                  <Inventory2Icon fontSize="inherit" />
                </Box>
              </Stack>
              <Typography fontSize={12} color="#5E5964" lineHeight={1.25}>
                {i18n.t('temp.private.list.common.archived')}
              </Typography>
            </Stack>

            <Stack sx={{ minWidth: 88 }}>
              <Stack direction="row" alignItems="center" gap={0.75}>
                <Typography fontSize={22} fontWeight={900} color="#3A3440" lineHeight={1}>
                  {stats.inputWeight}
                </Typography>
              </Stack>
              <Typography fontSize={12} color="#5E5964" lineHeight={1.25}>
                {i18n.t('temp.common.tons')}
              </Typography>
            </Stack>
          </Stack>
        </Box>
      </Box>
    </Stack>
  );
}
